<div class="title-bar title-bar--responsive">
  <div class="title-bar__heading">
    <h1>SUPER ADMIN</h1>
  </div>
</div>
<div class="flex">
  @if (pendingEplUsers | async; as pendingList) {
    <mat-card appearance="outlined">
      <mat-card-header>
        <mat-card-title>
          <h2>Pending EPL Users</h2>
        </mat-card-title>
        <mat-card-subtitle>
          EPL users with no school assignment — assign access to <strong>{{ schoolId }}</strong
          >.
        </mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        @if (pendingList.length === 0) {
          <p class="permission-users-empty">No pending EPL users.</p>
        } @else {
          <table class="permission-users-table">
            <thead>
              <tr>
                <th scope="col" id="pending-user-email">Email</th>
                <th scope="col" id="pending-user-epl">EPL</th>
                <th scope="col" id="pending-user-last-login">Last login</th>
                <th scope="col" id="pending-user-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (user of pendingList; track user.uid) {
                <tr>
                  <th scope="row" [attr.id]="rowHeaderId(user)">
                    <a [href]="'mailto:' + user.email" target="_blank" rel="noopener noreferrer">{{ user.email }}</a>
                  </th>
                  <td>
                    <mat-chip class="epl-badge" matTooltip="Signed in via College Board EPL" aria-label="Signed in via College Board EPL">EPL</mat-chip>
                  </td>
                  <td>
                    @if (lastLoginDate(user); as loginDate) {
                      {{ loginDate | date: "medium" }}
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    <button mat-raised-button color="primary" type="button" (click)="activate(user)" [attr.aria-labelledby]="rowHeaderId(user) + ' pending-user-actions'">Assign to school</button>
                    <button mat-button color="warn" type="button" (click)="deleteUser(user)" style="margin-left: 1rem" [attr.aria-labelledby]="rowHeaderId(user) + ' pending-user-actions'">Delete</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </mat-card-content>
    </mat-card>
  }

  @if (activeUsers | async; as activeList) {
    <mat-card appearance="outlined">
      <mat-card-header>
        <mat-card-title>
          <h2>User list for {{ schoolId }}</h2>
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if (activeList.length === 0) {
          <p class="permission-users-empty">No school permission users assigned yet.</p>
        } @else {
          <table class="permission-users-table">
            <thead>
              <tr>
                <th scope="col" id="active-user-email">Email</th>
                <th scope="col" id="active-user-epl">EPL</th>
                <th scope="col" id="active-user-last-login">Last login</th>
                <th scope="col" id="active-user-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (user of activeList; track user.uid) {
                <tr>
                  <th scope="row" [attr.id]="rowHeaderId(user)">
                    <a [href]="'mailto:' + user.email" target="_blank" rel="noopener noreferrer">{{ user.email }}</a>
                  </th>
                  <td>
                    @if (hasEpl(user)) {
                      <mat-chip class="epl-badge" matTooltip="Signed in via College Board EPL" aria-label="Signed in via College Board EPL">EPL</mat-chip>
                    }
                  </td>
                  <td>
                    @if (lastLoginDate(user); as loginDate) {
                      {{ loginDate | date: "medium" }}
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    <button mat-raised-button color="warn" type="button" (click)="deactivate(user)" [attr.aria-labelledby]="rowHeaderId(user) + ' active-user-actions'">Remove access</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </mat-card-content>
    </mat-card>
  }

  <mat-card appearance="outlined">
    <mat-card-header>
      <mat-card-title>
        <h2>School Settings</h2>
      </mat-card-title>
    </mat-card-header>
    <mat-card-content>
      @if (schoolSettingsLoading) {
        <p class="permission-users-empty">Loading school settings…</p>
      } @else {
        <form [formGroup]="schoolSettingsForm" (ngSubmit)="saveSchoolSettings()">
          <mat-form-field class="form-field">
            <mat-label>Status</mat-label>
            <mat-select formControlName="status">
              <mat-option value="">LIVE</mat-option>
              <mat-option value="COMING">Coming Soon</mat-option>
              <mat-option value="HOLD">On Hold / Paused</mat-option>
              <mat-option value="DEACTIVATED">Deactivated</mat-option>
            </mat-select>
          </mat-form-field>

          <ng-container formGroupName="schoolProperties">
            <mat-form-field class="form-field">
              <mat-label>Organization ID</mat-label>
              <input matInput type="number" formControlName="orgId" />
            </mat-form-field>
            <mat-form-field class="form-field">
              <mat-label>Methodology</mat-label>
              <mat-select formControlName="institutionalMethod">
                <mat-option [value]="true">Institutional Methodology</mat-option>
                <mat-option [value]="false">Federal Methodology</mat-option>
              </mat-select>
            </mat-form-field>
          </ng-container>

          <mat-form-field class="form-field">
            <mat-label>Go Live Date</mat-label>
            <input matInput type="date" formControlName="live" />
          </mat-form-field>

          @if (schoolSettingsError) {
            <p class="school-settings-error" role="alert">{{ schoolSettingsError }}</p>
          }

          <div class="school-settings-actions">
            <button mat-raised-button color="primary" type="submit" [disabled]="schoolSettingsSaving || !schoolSettingsForm.valid || schoolSettingsForm.pristine">
              {{ schoolSettingsSaving ? "Saving…" : "Save Changes" }}
            </button>
          </div>
        </form>
      }
    </mat-card-content>
  </mat-card>

  <mat-card appearance="outlined">
    <mat-card-header>
      <mat-card-title>
        <h2>Data Privacy</h2>
      </mat-card-title>
      <mat-card-subtitle>Enter a College Board Username to search for any user data</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <mat-form-field class="form-field" style="width: 14rem">
        <mat-label>College Board User ID</mat-label>
        <input matInput #eclUserId />
      </mat-form-field>
      @if (eclUserId.value) {
        <button mat-button color="primary" (click)="getEclUserRecords(eclUserId.value)">Get Records</button>
      }
      <p></p>
      @if (eclUserSessions | async; as userSessions) {
        <div>
          @if (userSessions.length === 0) {
            <p>No Records</p>
          }
          @if (userSessions.length > 0) {
            <div>
              <textarea matInput rows="30">{{ userSessions | json }}</textarea>
              <p></p>
              <button mat-raised-button color="warn" (click)="deleteUserSessions(userSessions)">Remove Records</button>
            </div>
          }
        </div>
      }
    </mat-card-content>
  </mat-card>
</div>
